import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  ChevronLeft,
  Filter,
  Search,
  CheckCircle2,
  AlertCircle,
  Timer,
  XCircle,
  Eye,
  FileText,
  Download,
  TrendingUp,
  Briefcase,
  CreditCard,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import Footer from "../../components/landing/Footer";

const STATUS_CONFIG = {
  pending: {
    label: "Pending Review",
    icon: Timer,
    color: "amber",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
    borderClass: "border-amber-200",
    iconClass: "text-amber-600",
  },
  approved: {
    label: "Approved",
    icon: CheckCircle2,
    color: "green",
    bgClass: "bg-green-50",
    textClass: "text-green-700",
    borderClass: "border-green-200",
    iconClass: "text-green-600",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    color: "red",
    bgClass: "bg-red-50",
    textClass: "text-red-700",
    borderClass: "border-red-200",
    iconClass: "text-red-600",
  },
  under_review: {
    label: "Under Review",
    icon: AlertCircle,
    color: "blue",
    bgClass: "bg-blue-50",
    textClass: "text-blue-700",
    borderClass: "border-blue-200",
    iconClass: "text-blue-600",
  },
};

export default function MyRegistrations() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
  });

  const calculateStats = useCallback((events) => {
    const stats = {
      total: events.length,
      pending: events.filter((e) => {
        const paymentStatus = (e.payment_status || "pending").toLowerCase();
        return paymentStatus === "pending";
      }).length,
      paid: events.filter((e) => {
        const paymentStatus = (e.payment_status || "pending").toLowerCase();
        return paymentStatus === "paid" || paymentStatus === "completed" || paymentStatus === "verified";
      }).length,
    };
    setStats(stats);
  }, []);

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both conference and trade_mission registrations so "My Registrations" shows all
      const [confResponse, tradeResponse] = await Promise.all([
        conferenceAndTradeMissionService.getAllRegisteredEvents("conference"),
        conferenceAndTradeMissionService.getAllRegisteredEvents("trade_mission"),
      ]);

      const conferenceEvents = confResponse?.status && Array.isArray(confResponse.data) ? confResponse.data : [];
      const tradeEvents = tradeResponse?.status && Array.isArray(tradeResponse.data) ? tradeResponse.data : [];

      // Merge and dedupe by registration id (in case backend ever returns same in both)
      const seen = new Set();
      const merged = [...conferenceEvents, ...tradeEvents].filter((reg) => {
        const id = reg.id ?? reg.registration_id ?? reg.application_id;
        if (id && seen.has(id)) return false;
        if (id) seen.add(id);
        return true;
      });

      // Sort by event date descending (most recent first)
      merged.sort((a, b) => {
        const dateA = a.event?.event_date || a.event_date || "";
        const dateB = b.event?.event_date || b.event_date || "";
        return dateB.localeCompare(dateA);
      });

      setRegistrations(merged);
      calculateStats(merged);
    } catch (error) {
      toast.error(
        error.response?.data?.reason || "Failed to fetch registrations"
      );
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  const applyFilters = useCallback(() => {
    let filtered = [...registrations];

    if (searchQuery) {
      filtered = filtered.filter(
        (reg) =>
          reg.event?.event_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          reg.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((reg) => {
        const paymentStatus = (reg.payment_status || "pending").toLowerCase();
        if (statusFilter === "paid") {
          return paymentStatus === "paid" || paymentStatus === "completed" || paymentStatus === "verified";
        }
        return paymentStatus === statusFilter;
      });
    }

    setFilteredRegistrations(filtered);
  }, [registrations, searchQuery, statusFilter]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const status = (paymentStatus || "pending").toLowerCase();
    
    if (status === "paid" || status === "completed" || status === "verified") {
      return {
        label: "Paid",
        icon: CheckCircle2,
        bgClass: "bg-green-50",
        textClass: "text-green-700",
        borderClass: "border-green-200",
        iconClass: "text-green-600",
      };
    } else if (status === "pending") {
      return {
        label: "Pending Payment",
        icon: Timer,
        bgClass: "bg-amber-50",
        textClass: "text-amber-700",
        borderClass: "border-amber-200",
        iconClass: "text-amber-600",
      };
    } else {
      return {
        label: "Payment Pending",
        icon: CreditCard,
        bgClass: "bg-gray-50",
        textClass: "text-gray-700",
        borderClass: "border-gray-200",
        iconClass: "text-gray-600",
      };
    }
  };

  const handleViewDetails = (registration) => {
    if (registration.event?.slug) {
      navigate(`/event-details/${registration.event.slug}`);
    }
  };

  const handleDownloadReceipt = (registration) => {
    navigate(`/registration-receipt/${registration.slug}`, {
      state: { registration },
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-orange-500/[0.08] via-amber-500/[0.06] to-transparent" aria-hidden />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-500/10 text-orange-600">
              <Briefcase size={20} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="mb-0.5 flex items-center justify-start">
                <span className="inline-flex items-center rounded-full border border-orange-200/70 bg-orange-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-orange-700">
                  Applications
                </span>
              </div>
              <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
                My Registrations
              </h1>
              <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm">
                Track your event applications and status
              </p>
            </div>
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
            <motion.button
              type="button"
              onClick={() => navigate(-1)}
              whileHover={{ x: -2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-orange-500/10 hover:text-orange-700 sm:flex-none"
            >
              <ChevronLeft size={20} strokeWidth={2} />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">Back</span>
            </motion.button>
            <motion.button
              type="button"
              onClick={() => navigate("/conference-trade-mission")}
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-semibold text-orange-700 shadow-sm transition-colors hover:bg-orange-500/10 hover:text-orange-800 sm:flex-none"
            >
              <Sparkles size={18} />
              <span className="sm:hidden">Browse</span>
              <span className="hidden sm:inline">Browse Events</span>
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </div>
      </header>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="grid grid-cols-2 gap-3 mb-6 sm:gap-4 sm:mb-8 md:grid-cols-3"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="p-3 bg-gradient-to-br from-purple-50 to-white rounded-lg border border-purple-200 shadow-sm sm:p-4 sm:rounded-xl"
            >
              <div className="flex justify-between items-center mb-1.5 sm:mb-2">
                <p className="text-[10px] sm:text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Total
                </p>
                <TrendingUp size={14} className="text-purple-600 sm:w-4 sm:h-4" />
              </div>
              <p className="text-xl font-bold text-purple-700 sm:text-2xl">
                {stats.total}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25 }}
              className="p-3 bg-gradient-to-br from-amber-50 to-white rounded-lg border border-amber-200 shadow-sm sm:p-4 sm:rounded-xl"
            >
              <div className="flex justify-between items-center mb-1.5 sm:mb-2">
                <p className="text-[10px] sm:text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Pending Payment
                </p>
                <Timer size={14} className="text-amber-600 sm:w-4 sm:h-4" />
              </div>
              <p className="text-xl font-bold text-amber-700 sm:text-2xl">
                {stats.pending}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="p-3 bg-gradient-to-br from-green-50 to-white rounded-lg border border-green-200 shadow-sm sm:p-4 sm:rounded-xl"
            >
              <div className="flex justify-between items-center mb-1.5 sm:mb-2">
                <p className="text-[10px] sm:text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Paid
                </p>
                <CheckCircle2 size={14} className="text-green-600 sm:w-4 sm:h-4" />
              </div>
              <p className="text-xl font-bold text-green-700 sm:text-2xl">
                {stats.paid}
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="p-4 mb-6 bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search
                  className="absolute top-3 left-3 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by event or company name..."
                  className="py-2.5 pr-4 pl-10 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="flex gap-2 items-center">
                <Filter size={18} className="text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg border border-gray-300 transition-all outline-none hover:bg-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="all">All Payment Status</option>
                  <option value="pending">Pending Payment</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
          </motion.div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-orange-200 animate-spin border-t-orange-500" />
                <div className="flex absolute inset-0 justify-center items-center">
                  <Briefcase size={24} className="text-orange-500" />
                </div>
              </div>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-20 text-center"
            >
              <div className="flex justify-center mb-6">
                <div className="flex justify-center items-center w-20 h-20 bg-gray-100 rounded-full">
                  <FileText size={40} className="text-gray-400" />
                </div>
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery || statusFilter !== "all"
                  ? "No results found"
                  : "No registrations yet"}
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                {searchQuery || statusFilter !== "all"
                  ? "Try adjusting your filters or search query"
                  : "Start by registering for an event"}
              </p>
              {!searchQuery && statusFilter === "all" && (
                <motion.button
                  onClick={() => navigate("/conference-trade-mission")}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md transition-all hover:from-orange-600 hover:to-orange-700"
                >
                  Browse Events
                </motion.button>
              )}
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
                {filteredRegistrations.map((registration, index) => {
                  const eventStatus = registration.event?.event_status || registration.status || "pending";
                  const statusConfig = getStatusConfig(eventStatus);
                  const StatusIcon = statusConfig.icon;
                  const eventData = registration.event || {};
                  const isConference = eventData.event_type?.includes("conference");
                  const paymentStatus = registration.payment_status || "pending";
                  const paymentStatusConfig = getPaymentStatusBadge(paymentStatus);
                  const PaymentStatusIcon = paymentStatusConfig.icon;

                  return (
                    <motion.div
                      key={registration.id || index}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex overflow-hidden flex-col bg-white rounded-lg border border-gray-200 shadow-sm transition-all sm:rounded-xl hover:border-orange-300 hover:shadow-lg group"
                    >
                      <div className="relative h-32 bg-gradient-to-br from-orange-100 via-blue-100 to-purple-100 sm:h-40">
                        {eventData.thumbnail && (
                          <img
                            src={eventData.thumbnail}
                            alt={eventData.event_title}
                            className="object-cover w-full h-full transition-transform group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/40" />

                        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-col gap-1.5 sm:gap-2">
                          <div
                            className={`flex gap-1 sm:gap-1.5 items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold backdrop-blur-sm rounded-full border ${statusConfig.bgClass} ${statusConfig.textClass} ${statusConfig.borderClass}`}
                          >
                            <StatusIcon size={10} className="sm:w-3 sm:h-3" />
                            <span className="hidden sm:inline">{statusConfig.label}</span>
                          </div>
                          <div
                            className={`flex gap-1 sm:gap-1.5 items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold backdrop-blur-sm rounded-full border ${paymentStatusConfig.bgClass} ${paymentStatusConfig.textClass} ${paymentStatusConfig.borderClass}`}
                          >
                            <PaymentStatusIcon size={10} className="sm:w-3 sm:h-3" />
                            <span className="hidden sm:inline">{paymentStatusConfig.label}</span>
                          </div>
                        </div>

                        <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3">
                          <span
                            className={`inline-flex px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-[9px] sm:text-xs font-bold text-white backdrop-blur-sm rounded-full border border-white/30 ${
                              isConference
                                ? "bg-blue-500/80"
                                : "bg-purple-500/80"
                            }`}
                          >
                            {isConference ? "Conference" : "Trade Mission"}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col flex-1 p-2.5 sm:p-4">
                        <h3 className="mb-2 text-xs font-bold leading-tight text-gray-900 transition-colors sm:mb-3 sm:text-base line-clamp-2 group-hover:text-orange-600">
                          {eventData.event_title || "Untitled Event"}
                        </h3>

                        <div className="flex-1 mb-2 sm:mb-4 space-y-1.5 sm:space-y-2">
                          {registration.company_name && (
                            <div className="flex gap-1.5 sm:gap-2 items-start">
                              <Briefcase
                                size={12}
                                className="flex-shrink-0 mt-0.5 text-gray-500 sm:w-3.5 sm:h-3.5"
                              />
                              <p className="text-[10px] sm:text-xs text-gray-600 line-clamp-1 leading-tight">
                                {registration.company_name}
                              </p>
                            </div>
                          )}

                          {((eventData.event_start_date || eventData.event_date) || (eventData.event_end_date || eventData.event_date)) && (
                            <div className="flex flex-wrap gap-1 sm:gap-2 items-center text-[10px] sm:text-xs text-gray-600">
                              <div className="flex gap-1 items-center">
                                <Calendar
                                  size={11}
                                  className="flex-shrink-0 text-emerald-600 sm:w-3.5 sm:h-3.5"
                                />
                                <span className="leading-tight">
                                  {(() => {
                                    const start = eventData.event_start_date || eventData.event_date;
                                    const end = eventData.event_end_date || eventData.event_date;
                                    return start && end && start !== end
                                      ? `${formatDate(start)} – ${formatDate(end)}`
                                      : formatDate(start || eventData.event_date);
                                  })()}
                                </span>
                              </div>
                              {(eventData.event_start_time || eventData.event_time || eventData.event_end_time) && (
                                <div className="flex gap-1 items-center">
                                  <span className="text-gray-400">•</span>
                                  <Clock
                                    size={11}
                                    className="flex-shrink-0 text-emerald-600 sm:w-3.5 sm:h-3.5"
                                  />
                                  <span className="leading-tight">
                                    {eventData.event_start_time && eventData.event_end_time && eventData.event_start_time !== eventData.event_end_time
                                      ? `${formatTime(eventData.event_start_time)} – ${formatTime(eventData.event_end_time)}`
                                      : formatTime(eventData.event_start_time || eventData.event_time)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {eventData.venue_name && (
                            <div className="flex gap-1.5 sm:gap-2 items-start">
                              <MapPin
                                size={11}
                                className="flex-shrink-0 mt-0.5 text-blue-600 sm:w-3.5 sm:h-3.5"
                              />
                              <span className="text-[10px] sm:text-xs text-gray-600 line-clamp-1 leading-tight">
                                {eventData.venue_name}
                              </span>
                            </div>
                          )}

                          {registration.application_id && (
                            <div className="hidden pt-2 mt-2 border-t border-gray-100 sm:block">
                              <p className="text-xs text-gray-500">
                                ID:{" "}
                                <span className="font-mono font-semibold text-gray-700">
                                  {registration.application_id}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-1.5 sm:gap-2">
                          <motion.button
                            onClick={() => handleViewDetails(registration)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex flex-1 gap-1 sm:gap-1.5 justify-center items-center px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-orange-700 bg-orange-50 rounded-md sm:rounded-lg border border-orange-200 transition-all hover:bg-orange-100"
                          >
                            <Eye size={12} className="sm:w-3.5 sm:h-3.5" />
                            <span className="hidden sm:inline">View</span>
                          </motion.button>

                          <motion.button
                            onClick={() => handleDownloadReceipt(registration)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex gap-1 sm:gap-1.5 justify-center items-center px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-gray-700 bg-gray-50 rounded-md sm:rounded-lg border border-gray-200 transition-all hover:bg-gray-100"
                          >
                            <Download size={12} className="sm:w-3.5 sm:h-3.5" />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </AnimatePresence>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
